import { ArrowRight, Bell, Check, Play, Settings, Sparkles, Store } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MainMenu } from '@/components/layout/shell/MainMenu';
import type { MainMenuItem } from '@/components/layout/shell/types';
import { ObserverProvider } from '@/components/observer/ObserverProvider';
import {
  assistantMode,
  assistantPatch,
  useObserver,
  type AssistantMode,
  type ObserverSeat,
} from '@/components/observer/observerContext';
import { toneOf } from '@/components/observer/observerEvents';
import { useObserverAnchor } from '@/components/observer/useObserverAnchor';
import { Badge, Button, PageHeader, Switch, Tabs, TabsList, TabsTrigger, cn } from '@/components/ui';
import { Panel } from '../foundation/screens/common';
import { Code } from './Section';

/*
  «Полёт ядра к событию» — мини-сцена помощника (spec-3 §13, spec-3-motion): свой ObserverProvider
  home="core" и свой ключ настроек — ни площадка /design/observer, ни «Основа» её не выключают. Дом —
  настоящее главное меню (знак с меткой data-core-home), окно с полосой вкладок и прокруткой, два блока-якоря.
  Разметка плана дома — та же, что в «Основе»: data-shell, data-shell-card, data-scrollport,
  data-observer-block; курьер читает её сам. Всё остальное о событиях — в «Основе», ряд «Помощник».
*/

/** Свои настройки сцены: режим, посадка и проверочные переключатели живут только здесь. */
const STORAGE_KEY = 'kaname.design.courier';

const SECTIONS: MainMenuItem[] = [{ id: 'marketplace', label: 'Marketplace', icon: Store }];

/** Ключ «нужен ответ» сцены: под ним держится ожидание до «Ответ дан». */
const ASK_KEY = 'ask:demo';

type SampleKind = 'notification' | 'done' | 'ask';
const SAMPLES: { value: SampleKind; label: string }[] = [
  { value: 'notification', label: 'Уведомление' },
  { value: 'done', label: 'Готово' },
  { value: 'ask', label: 'Нужен ответ' },
];

const ASSISTANT_MODES: { value: AssistantMode; label: string }[] = [
  { value: 'off', label: 'Выкл' },
  { value: 'home', label: 'На месте' },
  { value: 'guide', label: 'Показывает, где' },
];

const SEATS: { value: ObserverSeat; label: string }[] = [
  { value: 'shoulder', label: 'Левое плечо' },
  { value: 'corner', label: 'Правый угол' },
];

export function CourierScene() {
  return (
    <ObserverProvider home="core" storageKey={STORAGE_KEY}>
      <Scene />
    </ObserverProvider>
  );
}

function Scene() {
  const observer = useObserver();
  const { settings, setSettings } = observer;
  const [kind, setKind] = useState<SampleKind>('notification');
  const [unread, setUnread] = useState(3);
  // Дом: null — главная (ядро в знаке), иначе пункт с выделением (ядро — выделение).
  const [active, setActive] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const asking = observer.holds[ASK_KEY] === 'asking';
  const tools = useMemo<MainMenuItem[]>(
    () => [
      { id: 'ai', label: 'ИИ', icon: Sparkles, accent: 'ai' },
      { id: 'notifications', label: 'Уведомления', icon: Bell, badge: unread },
      { id: 'settings', label: 'Настройки', icon: Settings },
    ],
    [unread],
  );

  // Признак ставится сразу и без ядра (счётчик колокольчика); ядро только показывает, где это.
  const play = () => {
    if (kind === 'notification') {
      setUnread((n) => n + 1);
      observer.notify({ kind, anchorId: 'nav.notifications', key: 'demo.notify' });
    } else if (kind === 'done') {
      observer.notify({
        kind,
        anchorId: 'demo.op',
        key: 'demo.op',
        label: 'host-338: обслуживание завершено',
      });
    } else {
      observer.notify({
        kind,
        anchorId: ['demo.inc', 'demo.attention'],
        key: ASK_KEY,
        label: 'INC-2189 ждёт ответа',
      });
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div data-shell="" className="flex h-96 gap-3 rounded-lg border border-line bg-frame p-3">
        <div className={cn('flex shrink-0 transition-width', expanded ? 'w-60' : 'w-20')}>
          <MainMenu
            expanded={expanded}
            onToggleExpand={() => setExpanded((e) => !e)}
            homeActive={active === null}
            onHome={() => setActive(null)}
            activeId={active}
            sections={SECTIONS}
            tools={tools}
            // ИИ и уведомления открываются поверх — выделение на них не переезжает (как в «Основе»).
            onSelect={(id) => {
              if (id === 'marketplace' || id === 'settings') setActive(id);
            }}
            tone={toneOf(observer.state)}
            life="both"
          />
        </div>
        <section
          data-shell-card="pane"
          aria-label="Окно образца"
          className="relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-line bg-canvas shadow-inner-card"
        >
          <div data-slot="tab-strip" className="flex h-11 shrink-0 items-end bg-tabs pl-2 shadow-hairline-b">
            <Tabs value="home">
              <TabsList aria-label="Вкладки образца">
                <TabsTrigger value="home">Главная</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div data-scrollport="" className="min-h-0 flex-1 overflow-y-auto px-8 pt-6 pb-8">
            <PageHeader title="Главная" meta={<span>образец: два блока — цели событий</span>} />
            <div className="mt-4 grid grid-cols-2 gap-4">
              <Panel observe="demo.op" title="Обслуживание узла">
                <p className="text-sm text-fg-muted">host-338 · шаг 4 из 4 — завершение</p>
              </Panel>
              <Panel observe="demo.attention" title="Требует внимания">
                <IncRow />
              </Panel>
            </div>
          </div>
        </section>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <Segmented label="Событие" value={kind} onChange={setKind} options={SAMPLES} />
        <Button size="sm" variant="secondary" leftIcon={<Play />} onClick={play}>
          проиграть
        </Button>
        {asking && (
          <Button size="sm" variant="ghost" leftIcon={<Check />} onClick={() => observer.resolve(ASK_KEY)}>
            Ответ дан
          </Button>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <Segmented
          label="Помощник"
          value={assistantMode(settings)}
          onChange={(m) => setSettings(assistantPatch(m))}
          options={ASSISTANT_MODES}
        />
        <Segmented
          label="Посадка"
          value={settings.seat}
          onChange={(seat) => setSettings({ seat })}
          options={SEATS}
        />
        <Switch
          size="sm"
          checked={settings.targetRing}
          onCheckedChange={(targetRing) => setSettings({ targetRing })}
          label="Кольцо на цели"
        />
        <Switch
          size="sm"
          checked={settings.showRoute}
          onCheckedChange={(showRoute) => setSettings({ showRoute })}
          label="Показать дорогу"
        />
        <Switch
          size="sm"
          checked={settings.slowMo}
          onCheckedChange={(slowMo) => setSettings({ slowMo })}
          label="Замедлить ×4"
        />
      </div>
      <p className="text-xs text-fg-muted">
        Уведомление — капля идёт в левое поле меню и прислоняется к колокольчику. «Готово» и «Нужен ответ» —
        по порогу, стене и полке над блоками на левое плечо блока; у цели капля разворачивается в тело и
        отвечает жестом, потом возвращается домой. Чаще раза в 6 с — жест дома без полёта. Выбери пункт в меню
        — дом станет выделением, путь пойдёт через крышу. Тело сменное (<Code>CourierBody</Code>, проп{' '}
        <Code>body</Code>), жесты — <Code>EVENT_PLAN</Code>.
      </p>
    </div>
  );
}

/** Строка блока — цель «нужен ответ» (роль item): тело садится на блок, контур — на строку. */
function IncRow() {
  const meta = useMemo(() => ({ role: 'item' as const, label: 'INC-2189' }), []);
  const anchor = useObserverAnchor<HTMLDivElement>('demo.inc', meta);
  return (
    <div ref={anchor} className="flex items-center justify-between gap-3 rounded-md px-3 py-2 hover:bg-hover">
      <span className="truncate text-sm text-fg">INC-2189 · host-338 без связи</span>
      <Badge variant="urgent" solid>
        Sev 4
      </Badge>
    </div>
  );
}

function Segmented<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-2xs font-medium text-fg-subtle uppercase">{label}</span>
      <Tabs variant="pill" value={value} onValueChange={(v) => onChange(v as T)}>
        <TabsList>
          {options.map((o) => (
            <TabsTrigger key={o.value} value={o.value}>
              {o.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  );
}

/** Плитка Наблюдателя: где живёт тело и где показываются события. */
export function ObserverTile() {
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-line bg-card p-5">
      <div className="type-subheading text-fg">Наблюдатель — тело и события</div>
      <p className="text-sm text-fg-muted">
        Тело, жесты и финалы («разлив», «ёж») — на площадке «Наблюдатель»; там же стенды жидкого тела и
        лаборатория. События в работе показываются в «Основе»: ряд «Помощник» на верхней полосе — режим,
        «Событие» (уведомление, «готово», «нужен ответ») и спорные места переключателями (посадка, кольцо на
        цели).
      </p>
      <div className="flex flex-wrap gap-2">
        <Link
          to="/design/foundation"
          className="inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium text-brand-fg transition-colors duration-fast hover:bg-brand-soft"
        >
          События — в «Основе»
          <ArrowRight className="size-3.5" />
        </Link>
        <Link
          to="/design/observer"
          className="inline-flex h-9 items-center gap-1.5 rounded-md px-3 text-xs font-medium text-brand-fg transition-colors duration-fast hover:bg-brand-soft"
        >
          Площадка «Наблюдатель»
          <ArrowRight className="size-3.5" />
        </Link>
      </div>
    </div>
  );
}
